import { board as actions } from '../actions'
import * as R from 'ramda'
import moment from 'moment'

const getId = R.prop(':db/id')

// Reducers for
// store/action-methods/board.js
export const reducer = {}

reducer[actions.SET_BOARD] = ({ state, action }) => {
  return action.ops.replace
    ? R.mergeRight(state, action.board)
    : R.mergeDeepRight(state, action.board)
}

reducer[actions.SET_FEED] = ({
  state,
  action: {
    payload: { feed, pinnedToTop }
  }
}) => ({
  ...state,
  ':board/pinned-to-top': pinnedToTop || state[':board/pinnedToTop'],
  feed:
    feed ||
    state.feed.filter(
      post => !pinnedToTop || post[':db/id'] !== pinnedToTop[':db/id']
    )
})

reducer[actions.SET_BOARD_FEATURES] = ({
  state,
  action: { payload: features }
}) => ({
  ...state,
  ':board/features': features
})

reducer[actions.APPEND_FEED] = ({ state, action: { payload } }) => ({
  ...state,
  feed: [...(state.feed || []), ...payload]
})

reducer[actions.ADD_POST] = ({ state, action: { payload: newPost } }) => ({
  ...state,
  feed: state.feed ? [newPost, ...state.feed] : [newPost]
})

reducer[actions.UPDATE_POST] = ({ state, action: { payload: newPost } }) => ({
  ...state,
  ':board/pinned-to-top':
    R.path([':board/pinned-to-top', ':db/id'], state) === newPost[':db/id']
      ? newPost
      : state[':board/pinned-to-top'],
  feed: state.feed
    ? state.feed.map(post => {
        return post[':db/id'] === newPost[':db/id'] ? newPost : post
      })
    : [newPost]
})

reducer[actions.SET_PINNED_POST] = ({ state, action: { payload } }) => ({
  ...state,
  ':board/pinned-to-top': payload,
  feed: state.feed.filter(
    post => !payload || post[':db/id'] !== payload[':db/id']
  )
})

reducer[actions.SET_FEED_LENGTH] = ({ state, action: { payload } }) => ({
  ...state,
  feedLength: payload
})

reducer[actions.SET_FEED_COLLAPSED] = ({ state, action: { payload } }) => ({
  ...state,
  feedCollapsed: payload
})

reducer[actions.SET_POST_QUERY] = ({ state, action: { payload } }) => ({
  ...state,
  postQuery: payload
})

reducer[actions.UPDATE_GROUP_NAME] = ({ state, action }) => {
  const boardGroups = state[':board/groups'].map(grp => {
    if (
      R.path(['payload', 'data', ':db/id'], action) == R.path([':db/id'], grp)
    ) {
      return { ...grp, ':group/name': action.payload.data[':group/name'] }
    }
    return grp
  })
  return { ...state, ':board/groups': boardGroups }
}

reducer[actions.SET_BOARD_GROUPS] = ({ state, action }) => {
  return { ...state, ':board/groups': action.payload }
}

reducer[actions.SET_POST_VIEW] = ({ state, action: { payload: postId } }) => ({
  ...state,
  feed: state.feed.map(post => {
    return post[':db/id'] === postId
      ? {
          ...post,
          postViewedByLoggedInUser: true
        }
      : post
  })
})

reducer[actions.SET_MEMBERS] = ({
  state,
  action: { payload: membersData }
}) => ({
  ...state,
  '@board/members': membersData
})

reducer[actions.SET_NUDGE_SETTINGS] = ({
  state,
  action: { payload: nudgeSettings }
}) => ({
  ...state,
  ':board/settings': {
    ...R.propOr({}, ':board/settings', state),
    ':board-settings/nudge-settings': nudgeSettings
  }
})

reducer[actions.SET_PINNED_POST_VIEW] = ({
  state,
  action: { payload: postId }
}) => {
  const currentPin = state[':board/pinned-to-top']

  if (postId !== currentPin[':db/id']) return state

  return {
    ...state,
    ':board/pinned-to-top': {
      ...currentPin,
      postViewedByLoggedInUser: true
    }
  }
}

reducer[actions.SET_FEED_SCROLLING] = ({
  state,
  action: { payload: isScrolling }
}) => ({
  ...state,
  feedScrolling: isScrolling
})

reducer[actions.REMOVE_POST_IN_VIEW] = ({
  state,
  action: { payload: postId }
}) => ({
  ...state,
  postsInView: R.without([postId], state.postsInView)
})

reducer[actions.SET_DATERANGE] = ({ state, action }) => {
  return {
    ...state,
    dashboard: {
      ...state.dashboard,
      dateRange: action.dateRange
    }
  }
}

reducer[actions.SET_DASHBOARD] = ({ state, action }) => {
  const {
    dashboard: {
      user: selectedUser,
      dateRange: defaultDateRange,
      chartData: newChartData
    }
  } = state
  const boardCreatedAt = state[':system/created-at']
  const { userId, chartData } = action

  const mergeArray = R.pipe(
    R.map(R.indexBy(R.prop('date')) || []),
    R.reduce(R.mergeWith(R.mergeRight), {}),
    R.values
  )

  const dateChanged = !(
    newChartData.length === chartData.length &&
    Object.values(newChartData[0])[0] === Object.values(chartData[0])[0]
  )

  const dateRange =
    defaultDateRange.startDate === 'undefined' ||
    defaultDateRange.endDate === 'undefined'
      ? { startDate: moment(boardCreatedAt), endDate: moment().endOf('day') }
      : defaultDateRange
  const userNotChanged = R.path([':db/id'], selectedUser) === userId
  const user = userNotChanged
    ? selectedUser
    : state.followers.find(item => R.path([':db/id'], item) === userId)
  return {
    dashboard: {
      user,
      dateRange,
      chartData:
        userNotChanged || !dateChanged
          ? mergeArray([newChartData, chartData])
          : chartData
    }
  }
}

reducer[actions.REMOVE_DASHBOARD_LABEL] = ({ state, action }) => {
  const {
    dashboard: { chartData },
    dashboard
  } = state

  return {
    dashboard: {
      ...dashboard,
      chartData: R.map(R.dissoc(action.label), chartData)
    }
  }
}

reducer[actions.ADD_ADMIN] = ({ state, action: { payload: admin } }) => ({
  ...state,
  admins: [...R.propOr([], 'admins', state), admin]
})

reducer[actions.REMOVE_ADMIN] = ({ state, action: { payload: admin } }) => ({
  ...state,
  admins: R.propOr([], 'admins', state).filter(
    boardAdmin => getId(boardAdmin) !== getId(admin)
  )
})

reducer[actions.REMOVE_FOLLOWER] = ({
  state,
  action: { payload: follower }
}) => ({
  ...state,
  followers:
    state.followers &&
    state.followers.filter(
      existingFollower =>
        R.path([':db/id'], existingFollower) !== R.path([':db/id'], follower)
    ),
  admins: R.propOr([], 'admins', state).filter(
    boardAdmin => getId(boardAdmin) !== getId(follower)
  )
})

reducer[actions.ADD_VOTE] = ({
  state,
  action: {
    payload: { postId, optionId, user, voterId }
  }
}) => {
  const posts = [state[':board/pinned-to-top'], ...state.feed]
  const postIndex =
    posts && posts.findIndex(post => R.path([':db/id'], post) === postId)
  const post = posts && posts.find(p => R.path([':db/id'], p) === postId)
  const poll = post && post[':post/poll']
  const options = poll && poll[':poll/poll-options']
  const votedOptionIndex =
    options &&
    options.findIndex(option => R.path([':db/id'], option) === optionId)
  const votedOption =
    options && options.find(option => R.path([':db/id'], option) === optionId)
  const voters = votedOption && votedOption[':poll-option/voters']
  const [pinToTop, ...feed] = R.update(
    postIndex,
    {
      ...post,
      ':post/poll': {
        ...poll,
        ':poll/poll-options': R.update(
          votedOptionIndex,
          {
            ...votedOption,
            ':poll-option/voters': [
              ...(voters || []),
              {
                ':db/id': voterId,
                ':system/created-at': Date.now(),
                ':poll-voter/user': {
                  ':db/id': user[':db/id'],
                  ':user/username': user[':user/username']
                }
              }
            ]
          },
          options
        )
      }
    },
    posts
  )

  return {
    ...state,
    ':board/pinned-to-top': pinToTop,
    feed
  }
}

reducer[actions.RETRACT_VOTE] = ({
  state,
  action: {
    payload: { postId, optionId, userId }
  }
}) => {
  const posts = [state[':board/pinned-to-top'], ...state.feed]
  const postIndex =
    posts && posts.findIndex(post => R.path([':db/id'], post) === postId)
  const post = posts && posts.find(p => R.path([':db/id'], p) === postId)
  const poll = post && post[':post/poll']
  const options = poll && poll[':poll/poll-options']
  const votedOptionIndex =
    options &&
    options.findIndex(option => R.path([':db/id'], option) === optionId)
  const votedOption =
    options && options.find(option => R.path([':db/id'], option) === optionId)
  const voters = votedOption && votedOption[':poll-option/voters']

  const [pinToTop, ...feed] = R.update(
    postIndex,
    {
      ...post,
      ':post/poll': {
        ...poll,
        ':poll/poll-options': R.update(
          votedOptionIndex,
          {
            ...votedOption,
            ':poll-option/voters': voters.filter(
              voter => R.path([':poll-voter/user', ':db/id'], voter) !== userId
            )
          },
          options
        )
      }
    },
    posts
  )

  return {
    ...state,
    ':board/pinned-to-top': pinToTop,
    feed
  }
}

reducer[actions.REMOVE_TOPIC] = ({ state, action: { payload: topic } }) => ({
  ...state,
  ':board/topics':
    state[':board/topics'] &&
    state[':board/topics'].filter(
      existingTopic =>
        R.path([':db/id'], existingTopic) !== R.path([':db/id'], topic)
    )
})

reducer[actions.UPSERT_TOPIC] = ({ state, action: { payload: topic } }) => {
  const topics = state[':board/topics'] || []
  const topicIndex = topics.findIndex(
    existingTopic =>
      R.path([':db/id'], existingTopic) === R.path([':db/id'], topic)
  )

  return {
    ...state,
    ':board/topics':
      topicIndex > -1 ? R.update(topicIndex, topic, topics) : [...topics, topic]
  }
}

reducer[actions.UPSERT_BADGE] = ({ state, action: { payload: badge } }) => {
  const badges = state[':board/badges'] || []
  const badgeIndex = badges.findIndex(
    existingBadge =>
      R.path([':db/id'], existingBadge) === R.path([':db/id'], badge)
  )

  return {
    ...state,
    ':board/badges':
      badgeIndex > -1 ? R.update(badgeIndex, badge, badges) : [...badges, badge]
  }
}

reducer[actions.REMOVE_BADGE] = ({ state, action: { payload: badge } }) => ({
  ...state,
  ':board/badges':
    state[':board/badges'] &&
    state[':board/badges'].filter(
      existingBadge =>
        R.path([':db/id'], existingBadge) !== R.path([':db/id'], badge)
    )
})

reducer[actions.REMOVE_POST_BADGE] = ({
  state,
  action: {
    payload: { postId, badgeId }
  }
}) => {
  const ptt = R.prop(':board/pinned-to-top', state)
  const allPosts = [ptt, ...state.feed].filter(Boolean)

  const posts = allPosts.map(post => {
    const comments = post.comments
    const comment =
      comments && comments.find(c => R.path([':db/id'], c) === postId)
    if (R.path([':db/id'], post) === postId) {
      const postBadges = post[':post/badges'].filter(
        badge => R.path([':badge/id', ':db/id'], badge) !== badgeId
      )
      return {
        ...post,
        ':post/badges': postBadges
      }
    }

    if (comment) {
      const commentBadges = comment[':post/badges'].filter(
        badge => R.path([':badge/id', ':db/id'], badge) !== badgeId
      )
      const commentIndex =
        comments && comments.findIndex(c => R.path([':db/id'], c) === postId)
      return {
        ...post,
        comments: R.update(
          commentIndex,
          {
            ...comment,
            ':post/badges': commentBadges
          },
          comments
        )
      }
    }

    return post
  })

  const [pinToTop, ...feed] = posts

  return {
    ...state,
    feed: ptt ? feed : posts,
    ':board/pinned-to-top': ptt && pinToTop
  }
}

reducer[actions.ADD_POST_BADGE] = ({
  state,
  action: {
    payload: { badge, postId }
  }
}) => {
  const ptt = R.prop(':board/pinned-to-top', state)
  const allPosts = [ptt, ...state.feed].filter(Boolean)

  const posts = allPosts.map(post => {
    const comments = post.comments
    const comment =
      comments && comments.find(c => R.path([':db/id'], c) === postId)
    if (R.path([':db/id'], post) === postId) {
      const postBadges = post[':post/badges'] || []
      return {
        ...post,
        ':post/badges': R.append(badge, postBadges)
      }
    }

    if (comment) {
      const commentBadges = comment[':post/badges'] || []
      const commentIndex =
        comments && comments.findIndex(c => R.path([':db/id'], c) === postId)
      return {
        ...post,
        comments: R.update(
          commentIndex,
          {
            ...comment,
            ':post/badges': R.append(badge, commentBadges)
          },
          comments
        )
      }
    }

    return post
  })

  const [pinToTop, ...feed] = posts

  return {
    ...state,
    feed: ptt ? feed : posts,
    ':board/pinned-to-top': ptt && pinToTop
  }
}

reducer[actions.SET_NEW_POSTS] = ({ state, action: { payload } }) => ({
  ...state,
  newPosts: payload
})

reducer[actions.ADD_ACTIVITIES] = ({
  state,
  action: { payload: activities }
}) =>
  R.evolve({
    '@board/activity': (prev = []) => R.concat(prev, activities)
  })(state)

reducer[actions.SET_ACTIVITIES_TX] = ({
  state,
  action: { payload: newestTX }
}) => ({
  ...state,
  '@board/activity': {
    activityFeed: [
      ...((state['@board/activity'] && state['@board/activity'].activityFeed) ||
        [])
    ],
    newestTX
  }
})

reducer[actions.SET_CONTROLS_IN_VIEW] = ({ state, action: { payload } }) => ({
  ...state,
  controlsInView: payload
})

reducer[actions.SET_DISABLED_EMOJIS] = ({ state, action: { payload } }) => ({
  ...state,
  ':board/disabled-emojis': payload
})

reducer[actions.UPDATE_POINT_METRICS] = ({ state, action: { metrics } }) => {
  return R.mergeRight(state, metrics)
}

reducer[actions.UPDATE_FOLLOWERS_POINTS] = ({
  state,
  action: {
    payload: { userId, metrics }
  }
}) => {
  const followers = state.followers
  const followerIndex = R.findIndex(R.propEq(':db/id', userId), followers)
  const follower = followers[followerIndex]
  follower['@user/points-on-board'] = metrics

  return {
    ...state,
    followers: R.update(followerIndex, follower, followers)
  }
}

reducer[actions.ADD_READ_FLAG] = ({
  state,
  action: {
    payload: { postReadItem, isReadPost, postId }
  }
}) => {
  const posts = state.feed.map(post => {
    if (R.path([':db/id'], post) === postId) {
      return {
        ...post,
        ':post/reads': [postReadItem],
        readByUser: isReadPost
      }
    }
    return post
  })
  // For pinned post
  let pinnedPost = state[':board/pinned-to-top']

  if (R.path([':db/id'], pinnedPost) === postId) {
    pinnedPost = {
      ...pinnedPost,
      ':post/reads': [postReadItem],
      readByUser: isReadPost
    }
  }

  return {
    ...state,
    feed: posts,
    ':board/pinned-to-top': pinnedPost
  }
}

reducer[actions.ADD_COMMENT_READ_FLAG] = ({
  state,
  action: {
    payload: { postReadItem, isReadPost, postId, parentId }
  }
}) => {
  const posts = state.feed.map(post => {
    if (R.path([':db/id'], post) === parentId) {
      const postComments = post.comments.map(comment => {
        if (R.path([':db/id'], comment) === postId) {
          return {
            ...comment,
            ':post/reads': [postReadItem],
            readByUser: isReadPost
          }
        }
        return comment
      })

      return { ...post, comments: postComments }
    }
    return post
  })

  // For pinned post
  let pinnedPost = state[':board/pinned-to-top']

  if (R.path([':db/id'], pinnedPost) === parentId) {
    const postComments = pinnedPost.comments.map(comment => {
      if (R.path([':db/id'], comment) === postId) {
        return {
          ...comment,
          ':post/reads': [postReadItem],
          readByUser: isReadPost
        }
      }
      return comment
    })

    pinnedPost = { ...pinnedPost, comments: postComments }
  }

  return {
    ...state,
    feed: posts,
    ':board/pinned-to-top': pinnedPost
  }
}

// const addReaction = (post, reaction) => {
//   let newReaction = {
//     ':reaction/key': reaction[':reaction/key'],
//     ':system/created-at': Date.now(),
//     ':reaction/value': reaction[':reaction/value'],
//     ':reaction/author': reaction[':reaction/author'],
//     ':reaction/target': post[':db/id'],
//     new: true
//   }
//   post[':post/reactions'] = post[':post/reactions']
//     ? post[':post/reactions'].concat([newReaction])
//     : [newReaction]

//   postCreateReaction(newReaction)
//   return post
// }

// const removeReaction = (post, reaction) => {
//   post[':post/reactions'] = post[':post/reactions'].filter(
//     r => !R.propEq(':db/id', reaction[':db/id'], r)
//   )
//   postRetractReaction(reaction)
//   return post
// }

// const addOrRemoveReaction = (post, { reactions, author }) => {
//   // find reaction authored by user
//   let exists =
//     !!post[':post/reactions'] &&
//     post[':post/reactions'].find(
//       r =>
//         R.pathEq([':reaction/author', ':db/id'], author[':db/id'], r) &&
//         R.propEq(':reaction/key', reactions[0][':reaction/key'], r)
//     )

//   // if post has reaction, remove. else, create it
//   return !!exists
//     ? removeReaction(post, exists)
//     : addReaction(post, {
//         ...reactions[0],
//         ':reaction/author': author
//       })
// }

// const applyToPosts = (posts, targetId, fn, data) =>
//   posts.map(
//     p =>
//       p[':db/id'] === targetId
//         ? fn(p, data)
//         : p.comments && p.comments.length
//           ? (p.comments = applyToPosts(p.comments, targetId, fn, data)) && p
//           : p
//   )

// reducer[actions.CREATE_REACTION] = ({ state, action }) => {
//   let { reaction } = action

//   let posts = applyToPosts(
//     state.feed.posts,
//     reaction[':reaction/target'],
//     addReaction,
//     reaction
//   )

//   return {
//     feed: {
//       ...state.feed,
//       posts
//     }
//   }
// }

// reducer[actions.CREATE_OR_REMOVE_REACTION] = ({ state, action }) => {
//   let { reactions, reaction, author, target } = action
//   // we support arr of reactions, so if user clicks
//   // reaction arregation btn we don't know which
//   if (!reactions) {
//     reactions = [reaction]
//   }
//   let posts = applyToPosts(state.feed.posts, target, addOrRemoveReaction, {
//     reactions,
//     author
//   })

//   return {
//     feed: {
//       ...state.feed,
//       posts
//     }
//   }
// }

reducer[actions.ADD_BOARD_SHARE_TOKEN] = ({ state, action }) => {
  const { payload } = action

  return R.evolve({
    ':board/share-tokens': tokens => [...tokens, payload]
  })(state)
}

reducer[actions.TOGGLE_BOARD_SHARE_TOKEN] = ({ state, action }) => {
  const { payload } = action

  return R.evolve({
    ':board/share-tokens': R.map(t => {
      if (t[':db/id'] === payload) {
        return R.evolve({ ':board-share-token/enabled': enabled => !enabled })(
          t
        )
      }
      return t
    })
  })(state)
}

reducer[actions.SET_DID_TOTAL_MEMBERS_SURPASS_CUTOFF_SIZE] = ({
  state,
  action
}) => {
  const { didTotalMembersSurpassCutoffSize } = action.payload
  return R.assoc(
    'didTotalMembersSurpassCutoffSize',
    didTotalMembersSurpassCutoffSize
  )(state)
}
